import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import React, { Dispatch, SetStateAction } from 'react'

import { useConfirmation } from '@/app/contexts/confirmation.context'
import { noop } from '@/app/definitions'
import { usePrevious } from '@/app/helpers'
import { VoiceProps } from '@/app/module/campaigns/types'
import { FileUploadType, PersonalizationType } from '@/app/types'

import AudioMessage from './audio-message'
import DocumentMessage from './document-message'
import { BaseWhatsAppContent, loadDefaultTab, WhatsAppTab } from './helpers'
import ImageMessage from './image-message'
import TextMessage from './text-message'
import VideoMessage from './video-message'

type Props = Pick<
  VoiceProps,
  'files' | 'loading' | 'playId' | 'getFile' | 'onMicAccessError' | 'saveFileHandler' | 'setPlaying'
> & {
  audio?: string | null
  autoFocus?: boolean
  canRegisterTemplate: boolean
  defaultLanguage: string
  document?: string | null
  editable?: boolean
  enableMediaCaption: boolean
  /**
   * Indicates if the whatsapp message uses whatsapp buttons so we can show the confirmation message accordingly
   */
  hasButtons?: boolean
  /**
   * Indicates if the whatsapp message uses whatsapp list so we can show the confirmation message accordingly
   */
  hasList?: boolean
  image?: string | null
  initialTab?: WhatsAppTab
  info: string
  label: string
  personalizationList: PersonalizationType
  required?: boolean
  text?: string | null
  video?: string | null
  changeHandler: (value: BaseWhatsAppContent) => void
  onRecordingChange: (recording: boolean) => void
  onStop?: () => void
  onTabChange?: (tab: WhatsAppTab) => void
  removeMedia: () => void
  saveHandler: (file: FileUploadType) => Promise<void>
  /**
   * @description Callback to call when the tab is requested to be changed to check if a confirmation dialog should be shown
   * @param tab The new tab selected
   * @param currentContent The current content of the whatsapp message
   * @returns true if should show the confirmation dialog, false otherwise
   */
  shouldConfirm: (tab: WhatsAppTab, currentContent: BaseWhatsAppContent) => boolean
}

const WhatsApp: React.FC<Props> = (props) => {
  const {
    autoFocus,
    canRegisterTemplate,
    defaultLanguage,
    editable,
    enableMediaCaption,
    files = {},
    hasButtons,
    hasList,
    info,
    label,
    loading = false,
    personalizationList,
    playId,
    required = true,
    changeHandler,
    getFile,
    onMicAccessError,
    onRecordingChange,
    onStop,
    onTabChange,
    removeMedia = noop,
    saveFileHandler,
    saveHandler,
    setPlaying,
    shouldConfirm,
  } = props
  const [image, setImage] = React.useState(props.image || '')
  const [video, setVideo] = React.useState(props.video || '')
  const [audio, setAudio] = React.useState(props.audio || '')
  const [document, setDocument] = React.useState(props.document || '')
  const [tab, setTab] = React.useState(props.initialTab || loadDefaultTab(props))
  const [text, setText] = React.useState(props.text || '')
  const [error, setError] = React.useState('')
  const [videoError, setVideoError] = React.useState(false)

  useGetFile(props, 'image')

  useStateUpdate(props.audio, setAudio)
  useStateUpdate(props.document, setDocument)
  useStateUpdate(props.image, setImage)
  useStateUpdate(props.text, setText)
  useStateUpdate(props.video, setVideo)

  React.useEffect(() => {
    if (tab === WhatsAppTab.Audio) {
      setText('')
    }
  }, [tab])

  React.useEffect(() => {
    if (videoError) {
      setTimeout(() => {
        setVideoError(false)
      }, 3000)
    }
  }, [videoError])

  const handleMessageTypeSwitch = (selectedTab?: WhatsAppTab) => {
    if (!selectedTab) {
      return
    }

    setImage('')
    setAudio('')
    setVideo('')
    setDocument('')
    removeMedia()
    setTab(selectedTab)
    if (onTabChange) {
      onTabChange(selectedTab)
    }
  }

  const createConfirmation = useConfirmation()

  const handleTabChangeRequested = async (selectedTab: WhatsAppTab) => {
    const shouldShowConfirmation = shouldConfirm(selectedTab, {
      audio,
      document,
      image,
      text,
      video,
    })

    if (!shouldShowConfirmation) {
      handleMessageTypeSwitch(selectedTab)
      return
    }

    const confirmed = await createConfirmation({
      title: 'Change WhatsApp message type',
      content: confirmationText,
      cancelButtonLabel: 'Cancel',
      confirmButtonLabel: 'Confirm',
    })

    if (confirmed) {
      handleMessageTypeSwitch(selectedTab)
    }
  }

  const confirmationText = React.useMemo(() => {
    if (!hasButtons && !hasList) {
      return 'Switching message types will discard your existing content. Do you want to continue?'
    }
    if (hasButtons) {
      return 'Only text message can be used with WhatsApp Buttons, switching message types will discard your existing content, remove the buttons and change the message response to Specific Response. Do you want to continue?'
    }
    return 'Only text message can be used with a WhatsApp List, switching message types will discard your existing content, remove the list and change the message response to Specific Response. Do you want to continue?'
  }, [hasButtons, hasList])

  return (
    <div className="whatsapp-cont">
      <Tabs
        value={tab}
        indicatorColor="primary"
        onChange={(e, selectedTab: WhatsAppTab) => handleTabChangeRequested(selectedTab)}
      >
        {tabNames.map(({ label: tabLabel, value }, index) => (
          <Tab disabled={loading} label={tabLabel} key={`tab-${index}`} id={`tab-${index}`} value={value} />
        ))}
      </Tabs>
      {tab === WhatsAppTab.Text && (
        <TextMessage
          autoFocus={autoFocus}
          editable={editable}
          error={error}
          info={info}
          label={label}
          personalizationList={personalizationList}
          required={required}
          text={text}
          changeHandler={({ text: newText }) => {
            setText(newText || '')
            changeHandler({
              text: newText,
              image: null,
              video: null,
              document: null,
              audio: null,
            })
          }}
          onBlur={({ message: value }) => {
            if (!value && required) {
              setError('WhatsApp message needs to have text added')
            } else {
              setError('')
            }
          }}
          onErrorChange={setError}
        />
      )}
      {tab === WhatsAppTab.Image && (
        <ImageMessage
          enableMediaCaption={enableMediaCaption}
          files={files}
          image={image}
          personalizationList={personalizationList}
          text={text}
          changeHandler={({ text: newText }) => {
            setText(newText)
            changeHandler({
              text: newText,
              image,
              video: null,
              document: null,
              audio: null,
            })
          }}
          saveHandler={saveHandler}
        />
      )}
      {tab === WhatsAppTab.Audio && (
        <AudioMessage
          audio={audio}
          defaultLanguage={defaultLanguage}
          files={files}
          filterType="whatsapp"
          info={info}
          loading={loading}
          personalizationList={personalizationList}
          playId={playId}
          changeHandler={changeHandler}
          getFile={getFile}
          onMicAccessError={onMicAccessError}
          onRecordingChange={onRecordingChange}
          onStop={onStop}
          removeMedia={removeMedia}
          saveFileHandler={saveFileHandler}
          setPlaying={setPlaying}
        />
      )}
      {tab === WhatsAppTab.Video && (
        <VideoMessage
          canRegisterTemplate={canRegisterTemplate}
          enableMediaCaption={enableMediaCaption}
          files={files}
          personalizationList={personalizationList}
          text={text}
          video={video}
          changeHandler={({ text: newText }) => {
            setText(newText)
            changeHandler({
              text: newText,
              video,
              image: null,
              document: null,
              audio: null,
            })
          }}
          getFile={getFile}
          saveHandler={saveHandler}
        />
      )}
      {tab === WhatsAppTab.Document && (
        <DocumentMessage
          document={document}
          enableMediaCaption={enableMediaCaption}
          files={files}
          personalizationList={personalizationList}
          text={text}
          changeHandler={({ text: newText }) => {
            setText(newText)
            changeHandler({
              text: newText,
              document,
              image: null,
              video: null,
              audio: null,
            })
          }}
          saveHandler={saveHandler}
        />
      )}
    </div>
  )
}

export const tabNames = [
  {
    label: 'Text Message',
    value: 'text',
  },
  {
    label: 'Image',
    value: 'image',
  },
  {
    label: 'Voice',
    value: 'audio',
  },
  {
    label: 'Video',
    value: 'video',
  },
  {
    label: 'PDF',
    value: 'document',
  },
]

export const useGetFile = (props: Props, type: 'audio' | 'document' | 'image' | 'video') => {
  const { getFile } = props

  const prev = usePrevious(props[type])
  const current = props[type]

  React.useEffect(() => {
    // prevent infinite loop since getFile is not memorized in the parent component
    if (current && current !== prev) {
      getFile(current)
    }
  }, [current, prev, getFile])
}

/**
 * Call setValue with the value passed in the first argument
 * if the value is not undefined and the current state is different from the value
 */
export const useStateUpdate = <T extends string>(
  value: T | undefined | null,
  setValue: Dispatch<SetStateAction<T>>,
) => {
  React.useEffect(() => {
    setValue((s) => {
      if (value !== undefined && s !== value) {
        return (value || '') as T
      }
      return s
    })
  }, [value, setValue])
}

export default WhatsApp
